import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CloseButton,
  EmailField,
  ErrorAlert,
  ModalHeader,
  PasswordField,
  SubmitButton,
} from './sign-in-modal/sign-in-modal.parts'
import { useSignInModal } from './sign-in-modal/use-sign-in-modal'

interface SignInModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export function SignInModal({ isOpen, onClose, onSuccess }: SignInModalProps): ReactElement | null {
  const { t } = useTranslation('auth')
  const { t: tCommon } = useTranslation('common')
  const state = useSignInModal({ isOpen, onClose, onSuccess, t })

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={state.handleBackdropClick}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        ref={state.modalRef}
        className="animate-in fade-in zoom-in-95 relative w-full max-w-md duration-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="signin-title"
      >
        <div className="rounded-2xl border border-white/20 bg-white/10 p-8 shadow-2xl backdrop-blur-xl">
          <CloseButton
            onClose={onClose}
            disabled={state.isLoading}
            label={tCommon('actions.close')}
          />
          <ModalHeader t={t} />
          <form
            onSubmit={(event) => {
              void state.handleSubmit(event)
            }}
            className="space-y-5"
          >
            {state.error && <ErrorAlert message={state.error} />}
            <EmailField
              emailRef={state.emailRef}
              value={state.email}
              onChange={state.setEmail}
              disabled={state.isLoading}
              t={t}
            />
            <PasswordField
              value={state.password}
              onChange={state.setPassword}
              showPassword={state.showPassword}
              onToggleShow={state.toggleShowPassword}
              disabled={state.isLoading}
              t={t}
            />
            <SubmitButton isLoading={state.isLoading} t={t} />
          </form>
        </div>
      </div>
    </div>
  )
}
